import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, Send, Globe, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IndexNowAdmin = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [customUrls, setCustomUrls] = useState('');

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/indexnow/status`);
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('Error fetching IndexNow status:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSubmitAll = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/indexnow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ Successfully submitted ${data.urls_submitted || 'all'} URLs to IndexNow!`);
        fetchStatus();
      } else {
        alert('❌ Failed: ' + (data.error || data.message));
      }
    } catch (err) {
      alert('❌ Error submitting to IndexNow');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCustom = async () => {
    if (!customUrls.trim()) {
      alert('Please enter at least one URL');
      return;
    }
    
    const urls = customUrls.split('\n').map(u => u.trim()).filter(u => u);
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/indexnow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls })
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ Submitted ${urls.length} URLs to IndexNow!`);
        setCustomUrls('');
        fetchStatus();
      } else {
        alert('❌ Failed: ' + (data.error || data.message));
      }
    } catch (err) {
      alert('❌ Error submitting to IndexNow');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading IndexNow status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">⚡ IndexNow - Instant Indexing</h1>
          <p className="text-gray-600 text-sm mt-1">
            Submit URLs instantly to Bing, Yandex, Seznam, and Naver
          </p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={loading}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`p-6 rounded-xl ${status?.configured ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center gap-3">
            {status?.configured ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <XCircle className="w-8 h-8 text-red-600" />
            )}
            <div>
              <div className="font-semibold text-gray-800">API Status</div>
              <div className={status?.configured ? 'text-green-600' : 'text-red-600'}>
                {status?.configured ? 'Configured & Active' : 'Not Configured'}
              </div>
            </div>
          </div>
        </div>

        <div className={`p-6 rounded-xl ${status?.scheduler_running ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-teal-600" />
            <div>
              <div className="font-semibold text-gray-800">Auto-Submit</div>
              <div className="text-teal-600">{status?.next_run || 'Not scheduled'}</div>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl bg-blue-50 border border-blue-200">
          <div className="flex items-center gap-3">
            <Globe className="w-8 h-8 text-blue-600" />
            <div>
              <div className="font-semibold text-gray-800">Search Engines</div>
              <div className="text-blue-600 text-sm">Bing, Yandex, Seznam, Naver</div>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Submit */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Submit All URLs */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Submit All Site URLs
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Instantly submit all pages from your sitemap to search engines. 
            Use this after adding new pages or making significant changes.
          </p>
          <button
            onClick={handleSubmitAll}
            disabled={submitting}
            className="w-full py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit All URLs Now
              </>
            )}
          </button>
        </div>

        {/* Submit Custom URLs */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-500" />
            Submit Specific URLs
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Submit specific pages (one URL per line). Great for newly updated pages.
          </p>
          <textarea
            value={customUrls}
            onChange={(e) => setCustomUrls(e.target.value)}
            placeholder="https://fortworthdental.com/dentist-near-me/&#10;https://fortworthdental.com/best-dentist-fort-worth/"
            className="w-full h-24 p-3 border rounded-lg mb-3 text-sm"
          />
          <button
            onClick={handleSubmitCustom}
            disabled={submitting || !customUrls.trim()}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            <Send className="w-5 h-5" />
            Submit Custom URLs
          </button>
        </div>
      </div>

      {/* Recent Submissions */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-800">📋 Recent Submissions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Time</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Type</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">URLs</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {status?.recent_submissions?.map((sub, idx) => (
                <tr key={idx} className="border-t hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-600">
                    {new Date(sub.timestamp).toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      sub.type === 'automatic' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {sub.type === 'automatic' ? '🤖 Auto' : '👤 Manual'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right font-medium">{sub.urls_submitted}</td>
                  <td className="py-3 px-4 text-center">
                    {sub.success ? (
                      <span className="text-green-600 flex items-center justify-center gap-1">
                        <CheckCircle className="w-4 h-4" /> {sub.status_code}
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center justify-center gap-1">
                        <XCircle className="w-4 h-4" /> Failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {(!status?.recent_submissions || status.recent_submissions.length === 0) && (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-gray-500">
                    No recent submissions
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="font-semibold text-blue-800 mb-2">ℹ️ What is IndexNow?</h3>
        <p className="text-blue-700 text-sm mb-3">
          IndexNow is an instant indexing protocol supported by Bing, Yandex, Seznam, and Naver. 
          When you submit URLs, these search engines are notified immediately about new or updated content.
        </p>
        <p className="text-blue-700 text-sm">
          <strong>Note:</strong> Google does not support IndexNow. For Google, use Google Search Console's 
          URL Inspection tool or wait for natural crawling via your sitemap.
        </p>
      </div>
    </div>
  );
};

export default IndexNowAdmin;
